/**
 * La régie parle au serveur par ici : un jeton, des routes, des types.
 *
 * Le jeton vit dans `sessionStorage` : recharger la page ne déconnecte pas,
 * fermer l'onglet si. Il dure douze heures côté serveur ; un 401 ramène à
 * l'entrée.
 */

const CLE_JETON = "hoa.regie.jeton";

export function lireJeton(): string | null {
  try {
    return sessionStorage.getItem(CLE_JETON);
  } catch {
    return null;
  }
}

export function ecrireJeton(t: string | null): void {
  try {
    if (t) sessionStorage.setItem(CLE_JETON, t);
    else sessionStorage.removeItem(CLE_JETON);
  } catch {
    /* navigation privée : le jeton vaut pour la page */
  }
}

export class ErreurRegie extends Error {
  constructor(
    message: string,
    readonly statut: number,
  ) {
    super(message);
  }
}

let surNonAutorise: (() => void) | null = null;
export function quandNonAutorise(f: (() => void) | null): void {
  surNonAutorise = f;
}

/** Un appel à la régie. Toujours un corps JSON, même vide : le serveur refuse un POST JSON sans corps. */
export async function appel<T>(chemin: string, init?: { method?: string; corps?: unknown }): Promise<T> {
  const jeton = lireJeton();
  const methode = init?.method ?? "GET";
  const res = await fetch(chemin, {
    method: methode,
    headers: {
      "content-type": "application/json",
      ...(jeton ? { authorization: `Bearer ${jeton}` } : {}),
    },
    body: methode === "GET" ? undefined : JSON.stringify(init?.corps ?? {}),
  });
  const corps = (await res.json().catch(() => ({}))) as { error?: string };
  if (res.status === 401 && surNonAutorise) surNonAutorise();
  if (!res.ok) throw new ErreurRegie(corps.error ?? `Erreur ${res.status}`, res.status);
  return corps as T;
}

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface Annonceur {
  nom: string;
  image: string | null;
  phrase: string;
  /** La couleur de la marque (#rrggbb), vide : par défaut. */
  couleur?: string;
}

export interface Compte {
  id: string;
  identifiant: string;
  ville: boolean;
  annonceur: Annonceur;
}

export interface TribuVille {
  id: string | null;
  themeId: string;
  titre: string;
  nomTribu: string;
  couleur: string;
  formee: boolean;
  etape: string;
  membres: number;
  enAttente: number;
  arrives: number;
  reunis: boolean;
  seuil: number;
  capacite: number;
  chatFinitDansS: number | null;
  chatSansFin?: boolean;
  signalements: number;
  peutDemarrer: boolean;
  forcable: boolean;
  rdvAt: number;
  chatOuvreDansS: number | null;
  ouverteA?: number;
  chatOuvertA?: number | null;
  reunieA?: number | null;
}

export interface SalleVille {
  id: string;
  nom: string;
  secteurId: string;
  secteurNom: string;
  etat: string;
  humains: number;
  connectes: number;
  signalements: number;
  tribus: TribuVille[];
  parVeille: boolean;
  sujet: string | null;
  couleur: string | null;
  lieu: string | null;
  jamaisOuverte: boolean;
  debuteDansMs: number;
  ouverteDepuisMs: number;
  fermeDansMs: number;
}

export interface QuartierVille {
  id: string;
  nom: string;
  humains: number;
  salles: SalleVille[];
}

export interface SignalementCourt {
  id: string;
  sessionId: string;
  recuA: number;
  motif: string;
  phase: string;
  tribu: { nom: string; sujet: string; couleur: string; lieu: string };
  statut: string;
}

export interface TribuClose {
  sessionId: string;
  nom: string;
  couleur: string | null;
  quartier: string;
  closedAt: number;
  entres: number;
  reunis: number;
  signalements: number;
  purgeOk: boolean;
}

export interface Tableau {
  a: number;
  sante: { protocole: number; stockage: string; depuisMs: number; memoireMo: number; connexions: number };
  veille: { videsVoulues: number; maxOuvertes: number };
  totaux: { humains: number; salles: number; ouvertes: number; vides: number; tribusFormees: number; reunies: number; signalements: number };
  quartiers: QuartierVille[];
  portes: { entrees: boolean; regie: boolean };
  signalementsVille: SignalementCourt[];
  annonce: { texte: string; a: number; jusqua: number; secteurId: string | null } | null;
  closes: TribuClose[];
}

export interface ReglesVille {
  seuil: number;
  capacite: number;
  messagesParPersonne: number;
  dureeChatMin: number;
  rayonM: number;
  delaiAbandonMin: number;
  texteTransparence: string;
}

export interface LieuAjoute {
  name: string;
  landmark: string;
  address: string;
  coords: { lat: number; lon: number };
}

export interface Creneau {
  jour: number;
  debut: string;
  fin: string;
}

export interface ReglagesVille {
  sessions: Creneau[];
  modifiable: boolean;
  fichier: string | null;
  regles: ReglesVille;
  /** Deux réglages de soirée tenus dans le fichier des interrupteurs. */
  interrupteurs?: { montrerRencontres: boolean; exigerProximite: boolean; couleurArrivee?: boolean };
  quartiers: Array<{ id: string; nom: string; ville: string; regie?: boolean; usine: string[]; ajoutees: LieuAjoute[] }>;
}

export interface Signalement {
  id: string;
  recuA: number;
  sessionId: string;
  compteId: string;
  evenementId: string | null;
  tribu: { nom: string; sujet: string; couleur: string; lieu: string };
  motif: string;
  phase: string;
  message: string | null;
  auteur: string;
  cible: string | null;
  eviter: boolean;
  statut: "nouveau" | "en_cours" | "traite";
  decision: { type: string; motif: string; a: number } | null;
  aSanction?: boolean;
  contestation?: { texte: string; a: number } | null;
  levee?: number | null;
  /** La photo de profil signalée (motif « photo »), effacée après la décision. */
  photo?: string | null;
}

export interface Question {
  id: string;
  text: string;
  a: string;
  b: string;
  c: string;
}

export interface ReglesEvenement {
  minimum: number;
  maximum: number;
  chatAvantMin: number;
  dureeChatMin: number | null;
  messagesParPersonne: number | null;
  rayonM: number;
  delaiAbandonMin: number;
}

export interface TableVue {
  id: string;
  nom: string;
  sujet: string;
  point: string;
  repere: string;
  rdvAt: number;
  minimum: number;
  maximum: number;
  couleurId: string;
  couleur: string;
  questions: Question[];
  sessionId: string | null;
  phase: string;
  membres: number;
  arrives: number;
  reunie: boolean;
  chatOuvreDansS: number | null;
  chatFinitDansS: number | null;
  chatSansFin: boolean;
  forcable: boolean;
  signalements: number;
  tribuId: string | null;
  /** Pour le fil de la soirée. */
  ouverteA?: number | null;
  chatOuvertA?: number | null;
  reunieA?: number | null;
  /** Fin du chat ouvert ; null : jusqu'à la fin de l'événement. */
  chatFinA?: number | null;
}

export interface EvenementVue {
  id: string;
  nom: string;
  lieu: { nom: string; adresse: string; lat: number; lon: number };
  /** Sans adresse : on n'y entre que par le QR code. */
  sansZone?: boolean;
  debut: number;
  fin: number;
  zoneM: number;
  regles: ReglesEvenement;
  conservationJours: number;
  prevenir: boolean;
  textes: { accueil: string; donnees: string };
  termineLe: number | null;
  ouvert: boolean;
  tables: TableVue[];
  plan?: PlanStandVue;
}

/** Le plan du stand, en mètres (voir apps/api/src/plan.ts). */
export interface PlanStandVue {
  largeurM: number;
  profondeurM: number;
  elements: Array<{
    id: string;
    type: "table" | "comptoir" | "ecran" | "mur" | "entree" | "salon" | "plante";
    x: number;
    y: number;
    w: number;
    h: number;
    rot: number;
    tableId: string | null;
    label: string;
  }>;
}

export interface EvenementPasse {
  id: string;
  nom: string;
  debut: number;
  participants: number;
  tables: number;
  messages: number;
  gardeJusqua: number | null;
}

export interface Direct {
  a: number;
  evenement: EvenementVue;
  presents: number;
  aUneTable: number;
  sansTable: number;
  reunies: number;
  signalements: number;
  alertes: Array<{ tableId: string; nom: string; sujet: string; couleur: string; dansS: number; membres: number; minimum: number }>;
}

export interface Bilan {
  id: string;
  nom: string;
  lieu: string;
  debut: number;
  fin: number;
  termineLe: number | null;
  gardeJusqua: number | null;
  participants: number;
  inscriptions: number;
  tables: number;
  tablesReunies: number;
  messages: number;
  rencontresMoyennes: number;
  frequentation: Array<{ at: number; n: number }>;
  parTable: Array<{
    id: string;
    nom: string;
    sujet: string;
    couleurId: string;
    point: string;
    rdvAt: number;
    inscrits: number;
    arrives: number;
    messages: number;
    reunieA: number | null;
    annulee: boolean;
  }>;
  questions: Array<{ tableId: string; table: string; questionId: string; question: string; reponses: Array<{ reponse: string; n: number }> }>;
}

export interface BilanComplet {
  bilan: Bilan;
  messages: Array<{ tableId: string; pseudo: string; text: string; at: number }>;
  tables: Array<{ id: string; nom: string; sujet: string; couleur: string }>;
  signalements: Signalement[];
  annonceur: Annonceur;
}

/* ------------------------------------------------------------------ */
/* Mots et nombres                                                     */
/* ------------------------------------------------------------------ */

const TZ = "Europe/Paris";

export const heure = (ms: number): string =>
  new Date(ms).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: TZ });

export const jourCourt = (ms: number): string =>
  new Date(ms).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", timeZone: TZ });

export const jourLong = (ms: number): string => {
  const s = new Date(ms).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", timeZone: TZ });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const dateCourte = (ms: number): string =>
  new Date(ms).toLocaleDateString("fr-FR", { day: "numeric", month: "short", timeZone: TZ });

/** « aujourd'hui 21:31 », « hier 23:05 », « 27 sept. 22:10 ». */
export function quand(ms: number, maintenant: number): string {
  const j = (x: number): string => new Date(x).toLocaleDateString("fr-FR", { timeZone: TZ });
  if (j(ms) === j(maintenant)) return `Aujourd'hui ${heure(ms)}`;
  if (j(ms) === j(maintenant - 86_400_000)) return `Hier ${heure(ms)}`;
  return `${dateCourte(ms)} ${heure(ms)}`;
}

export function minutes(s: number): string {
  const m = Math.max(0, Math.round(s / 60));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}`;
}

export function mmss(s: number): string {
  const x = Math.max(0, Math.round(s));
  const h = Math.floor(x / 3600);
  const m = Math.floor((x % 3600) / 60);
  const r = x % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`;
}

export function dureeLongue(ms: number): string {
  const m = Math.floor(ms / 60000);
  const j = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  if (j > 0) return `${j} j ${h} h`;
  if (h > 0) return `${h} h ${String(m % 60).padStart(2, "0")}`;
  return `${m} min`;
}

export const pluriel = (n: number, un: string, plusieurs: string): string => (n > 1 ? plusieurs : un);

export const MOTIFS: Record<string, string> = {
  propos: "Propos dans le chat",
  insistance: "Insistance",
  comportement: "Comportement au rendez-vous",
  photo: "Photo de profil",
  autre: "Autre",
  illicite: "Contenu illicite",
  apres: "Problème après une soirée",
};

export const MOMENTS: Record<string, string> = {
  inscriptions: "pendant les inscriptions",
  chat: "pendant le chat",
  "rendez-vous": "au rendez-vous",
  réunie: "après la rencontre",
  annulée: "après l'annulation",
  "après la soirée": "après la soirée",
  "hors tribu": "hors tribu",
};

export const DECISIONS: Record<string, string> = {
  classer: "Classé",
  avertir: "Averti",
  suspendre: "Suspendu 7 jours",
  bannir: "Banni",
  retirer: "Contenu retiré",
};

/** Les quatre étapes d'une tribu, pour la frise. */
export function rangPhase(etape: string): number {
  switch (etape) {
    case "chat":
      return 2;
    case "rendez-vous":
    case "rendezvous":
      return 3;
    case "réunie":
    case "reunie":
      return 4;
    case "annulée":
    case "annulee":
    case "close":
      return 0;
    default:
      return 1;
  }
}

export const LIBELLE_PHASE: Record<string, string> = {
  inscriptions: "Inscriptions",
  chat: "Chat",
  "rendez-vous": "Rendez-vous",
  rendezvous: "Rendez-vous",
  réunie: "Réunie",
  reunie: "Réunie",
  annulée: "Annulée",
  annulee: "Annulée",
  close: "Close",
};

/** Le fuseau de Lyon, pour lire et écrire une heure dans un champ. */
export function versChampDate(ms: number): string {
  const p = new Intl.DateTimeFormat("fr-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(ms));
  return p;
}

export function versChampHeure(ms: number): string {
  return new Date(ms).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: TZ, hour12: false });
}

/** « 2026-10-02 » + « 19:15 », lus à Lyon → millisecondes. */
export function depuisChamps(date: string, heureTexte: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const h = /^(\d{1,2}):(\d{2})$/.exec(heureTexte);
  if (!m || !h) return null;
  const utc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(h[1]), Number(h[2]));
  // Le décalage de Lyon à cet instant (une ou deux heures selon la saison).
  const lu = new Date(utc).toLocaleString("en-US", { timeZone: TZ, hour12: false });
  const vu = Date.parse(`${lu} UTC`);
  return utc - (vu - utc);
}
